import React, { useState, useEffect } from "react";
import { baseURL } from "../../App";
import "./admin.css";
import axios from "axios";
import Cookies from "js-cookie";
import { FaTrash, FaEdit, FaSave } from "react-icons/fa";

const HeaderAdmin = () => {
  const API_URL = `${baseURL}/api/header`;
  const [headerData, setHeaderData] = useState({
    works: { en: [], fr: [] },
    cvLinks: { en: "", fr: "" },
    image: "",
    _id: null,
  });

  const [newWork, setNewWork] = useState({ en: "", fr: "" });
  const [editingIndex, setEditingIndex] = useState(-1);
  const [allHeaderData, setAllHeaderData] = useState([]);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const token = Cookies.get("token");

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        const response = await axios.get(API_URL, { withCredentials: true });

        setAllHeaderData(response.data);

        // Set initial header data, ensuring correct structure:
        const initialHeaderData =
          response.data.length > 0
            ? response.data[response.data.length - 1]
            : {
                works: { en: [], fr: [] },
                cvLinks: { en: "", fr: "" },
                image: "",
                _id: null,
              };

        setHeaderData(initialHeaderData);
      } catch (err) {
        console.error("Error fetching Header data:", err);
      }
    };

    fetchHeaderData();
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "en" || name === "fr") {
      setHeaderData((prevData) => ({
        ...prevData,
        cvLinks: {
          ...prevData.cvLinks,
          [name]: value,
        },
      }));
    } else if (name === "image") {
      setHeaderData((prevData) => ({
        ...prevData,
        image: files[0],
      }));
    } else {
      setHeaderData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleAddWork = () => {
    if (newWork.en.trim() !== "" && newWork.fr.trim() !== "") {
      setHeaderData((prevData) => ({
        ...prevData,
        works: {
          en: [...prevData.works.en, newWork.en.trim()],
          fr: [...prevData.works.fr, newWork.fr.trim()],
        },
      }));
      setNewWork({ en: "", fr: "" });
    }
  };

  const handleEditWork = (index) => {
    setNewWork({
      en: headerData.works.en[index],
      fr: headerData.works.fr[index],
    });
    setEditingIndex(index);
  };

  const handleSaveWork = () => {
    // Check both languages
    const updatedWorksEn = [...headerData.works.en];
    const updatedWorksFr = [...headerData.works.fr];
    updatedWorksEn[editingIndex] = newWork.en.trim();
    updatedWorksFr[editingIndex] = newWork.fr.trim();
    setHeaderData((prevData) => ({
      ...prevData,
      works: { en: updatedWorksEn, fr: updatedWorksFr },
    }));
    setNewWork({ en: "", fr: "" });
    setEditingIndex(-1);
  };

  const handleRemoveWork = (index) => {
    setHeaderData((prevData) => ({
      ...prevData,
      works: {
        en: prevData.works.en.filter((_, i) => i !== index),
        fr: prevData.works.fr.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSelectChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === "new") {
      resetForm();
    } else {
      const selectedData =
        allHeaderData.find((item) => item._id === selectedId) || {};
      setHeaderData(selectedData);
    }
  };

  const handleCreate = async () => {
    const data = new FormData();
    // Use 'file' key and bracket notation for nested data
    if (headerData.image instanceof File) {
      data.append("file", headerData.image);
    }
    data.append("cvLinks[en]", headerData.cvLinks.en);
    data.append("cvLinks[fr]", headerData.cvLinks.fr);
    headerData.works.en.forEach((work, i) =>
      data.append(`works[en][${i}]`, work),
    );
    headerData.works.fr.forEach((work, i) =>
      data.append(`works[fr][${i}]`, work),
    );

    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      setConfirmationMessage("Data created successfully!");
      setAllHeaderData((prev) => [...prev, response.data]);
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Error creating data.",
      );
      console.error("Create Error:", err.response?.data);
    }
  };

  const handleUpdate = async () => {
    if (!headerData._id) {
      // console.log("No ID selected for update.");
      return;
    }

    const data = new FormData();
    if (headerData.image instanceof File) {
      data.append("file", headerData.image);
    }
    data.append("cvLinks[en]", headerData.cvLinks.en);
    data.append("cvLinks[fr]", headerData.cvLinks.fr);
    headerData.works.en.forEach((work, i) =>
      data.append(`works[en][${i}]`, work),
    );
    headerData.works.fr.forEach((work, i) =>
      data.append(`works[fr][${i}]`, work),
    );

    try {
      // Use PATCH for updates
      const response = await axios.patch(`${API_URL}/${headerData._id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      setConfirmationMessage("Data updated successfully!");

      setAllHeaderData((prev) =>
        prev.map((item) =>
          item._id === headerData._id ? response.data : item,
        ),
      );
    } catch (err) {
      const msg = err.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Error updating data.",
      );
      console.error("Update Error:", err.response?.data);
    }
  };

  const handleDelete = async () => {
    if (!headerData._id) return;

    try {
      await axios.delete(`${API_URL}/${headerData._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      setConfirmationMessage("Data deleted successfully!");
      setAllHeaderData((prev) =>
        prev.filter((item) => item._id !== headerData._id),
      );
      resetForm();
    } catch (err) {
      console.error("Error deleting Header data:", err);
      setConfirmationMessage(err.message);
    }
  };

  const resetForm = () => {
    setHeaderData({
      works: { en: [], fr: [] },
      cvLinks: { en: "", fr: "" },
      image: "",
      _id: null,
    });
  };

  const createDisabled = headerData._id !== null;
  const updateDisabled = headerData._id === null;
  const deleteDisabled = headerData._id === null;

  return (
    <div className="admin-section">
      <h2>Header Management</h2>
      <select
        className="select-item"
        onChange={handleSelectChange}
        value={headerData._id || "new"}
      >
        <option value="new" className="select-item__option">
          New Header
        </option>
        {allHeaderData.map(({ _id, image }) => (
          <option key={_id} value={_id} className="select-item__option">
            {image}
          </option>
        ))}
      </select>

      <form onSubmit={(e) => e.preventDefault()}>
        <div>
          <h3>Add Experience Tags</h3>
          <div className="add-work-section">
            <div className="form-group">
              <input
                type="text"
                placeholder="Add new work (EN)"
                value={newWork.en}
                onChange={(e) =>
                  setNewWork((prev) => ({ ...prev, en: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="Add new work (FR)"
                value={newWork.fr}
                onChange={(e) =>
                  setNewWork((prev) => ({ ...prev, fr: e.target.value }))
                }
              />
            </div>
            <button
              type="button"
              className="btn btnSecondary"
              onClick={handleAddWork}
            >
              Add Tag
            </button>
          </div>
        </div>
        {/* Works Section */}
        <h4>Experience Tags List</h4>
        {headerData.works &&
          headerData.works.en &&
          headerData.works.en.map((work, index) => (
            <div key={index} className="work-item">
              {editingIndex === index ? (
                <>
                  <input
                    type="text"
                    placeholder="Work (EN)"
                    value={newWork.en}
                    onChange={(e) =>
                      setNewWork((prev) => ({ ...prev, en: e.target.value }))
                    }
                  />
                  <input
                    type="text"
                    placeholder="Work (FR)"
                    value={newWork.fr}
                    onChange={(e) =>
                      setNewWork((prev) => ({ ...prev, fr: e.target.value }))
                    }
                  />
                </>
              ) : (
                <>
                  <span>{work} (EN)</span>
                  <span>{headerData.works.fr[index]} (FR)</span>
                </>
              )}

              <div className="icon-buttons">
                {editingIndex === index ? (
                  <FaSave className="save-icon" onClick={handleSaveWork} />
                ) : (
                  <FaEdit
                    className="edit-icon"
                    onClick={() => handleEditWork(index)}
                  />
                )}
                <FaTrash
                  className="delete-icon"
                  onClick={() => handleRemoveWork(index)}
                />
              </div>
            </div>
          ))}

        {/* CV Links Section */}
        <h3>CV Links</h3>
        <div className="form-group">
          <input
            type="url"
            name="en"
            placeholder="CV Link (EN)"
            value={headerData.cvLinks.en}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <input
            type="url"
            name="fr"
            placeholder="CV Link (FR)"
            value={headerData.cvLinks.fr}
            onChange={handleChange}
          />
        </div>

        {/* Image Section */}
        <h3>Header Image</h3>
        {headerData.image && typeof headerData.image === "string" && (
          <img
            src={headerData.image}
            alt="Header"
            className="img-preview"
            style={{ maxWidth: "100px", display: "block", margin: "10px 0" }}
          />
        )}
        {headerData.image && headerData.image instanceof File && (
          <p>New image selected: {headerData.image.name}</p>
        )}
        <input type="file" name="image" onChange={handleChange} />

        {/* Buttons */}
        <div className="btn-actions">
          <button
            className="btn btnPrimary"
            type="button"
            onClick={() => {
              if (window.confirm("Create this header entry?")) {
                handleCreate();
              }
            }}
            disabled={createDisabled}
          >
            Create
          </button>
          <button
            className="btn btnSecondary"
            type="button"
            onClick={() => {
              if (window.confirm("Update this header entry?")) {
                handleUpdate();
              }
            }}
            disabled={updateDisabled}
          >
            Update
          </button>
          <button
            className="btn btnDanger"
            type="button"
            onClick={() => {
              if (
                window.confirm("Are you sure you want to delete this header?")
              ) {
                handleDelete();
              }
            }}
            disabled={deleteDisabled}
          >
            Delete
          </button>
        </div>
      </form>

      {confirmationMessage && (
        <div className="confirmation-message">{confirmationMessage}</div>
      )}
    </div>
  );
};

export default HeaderAdmin;
